import React, { useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import _BaseHotelFlightsToggleButton from './_BaseHotelFlightsToggleButton'
import useBedbankPackageViews from 'hooks/useBedbankPackageViews'
import { min } from 'lib/array/arrayUtils'

interface Props {
  offer: App.BedbankOffer;
  rooms?: Array<App.Occupants>;
  checkIn?: string;
  checkOut?: string;
  flightOrigin?: App.Airport;
}

function BedbankHotelFlightsToggleButton(props: Props) {
  const {
    offer,
    rooms,
    checkIn,
    checkOut,
    flightOrigin,
  } = props

  const flightPrice = useBedbankFlightPrice(offer, {
    rooms,
    startDate: checkIn,
    endDate: checkOut,
    originAirportCode: flightOrigin?.code,
  })

  const [packageViews, fetchingRates] = useBedbankPackageViews(offer)
  const showAll = packageViews.filter(p => p.available).length === 0

  const availablePackageViews = useMemo(() => packageViews.filter(
    packageView => packageView.show && (packageView.available || showAll),
  ), [packageViews, showAll])

  const { hotelPrice, hotelMemberPrice, bundlePrice, bundleMemberPrice } = useMemo(() => {
    const getCheapestRatePrice = (rates: Array<App.BedbankRate>) => {
      if (rates.length > 0) {
        const cheapestRate = rates.reduce((prev, curr) => {
          const prevTotal = prev.totals.total
          const currTotal = curr.totals.total
          return prevTotal <= currTotal ? prev : curr
        })

        return {
          price: cheapestRate.totals.total,
          memberPrice: cheapestRate.totals.memberTotal ?? 0,
        }
      }

      return {
        price: 0,
        memberPrice: 0,
      }
    }

    // Get prices for all available package views
    const hotelPrices = availablePackageViews
      .map(view => getCheapestRatePrice(view.rates))
      .filter(price => price.price > 0)

    if (hotelPrices.length === 0) {
      return {
        hotelPrice: 0,
        hotelMemberPrice: 0,
        bundlePrice: 0,
        bundleMemberPrice: 0,
      }
    }

    const bundlePrices = availablePackageViews
      .map(view => getCheapestRatePrice([...view.rates, ...view.bundleRates]))
      .filter(price => price.price > 0)

    // Find the cheapest price among all package views
    const cheapestHotelPrice = min(hotelPrices, price => price.price)!
    const cheapestBundlePrice = min(bundlePrices, price => price.price)!

    return {
      hotelPrice: cheapestHotelPrice.price,
      hotelMemberPrice: cheapestHotelPrice.memberPrice,
      bundlePrice: cheapestBundlePrice.price,
      bundleMemberPrice: cheapestBundlePrice.memberPrice,
    }
  }, [availablePackageViews])

  const showMemberPrice = hotelMemberPrice > 0
  const hotelTotalPrice = showMemberPrice ? hotelMemberPrice : hotelPrice
  const bundleTotalPrice = showMemberPrice ? bundleMemberPrice : bundlePrice
  const hotelFlightTotalPrice = bundleTotalPrice > 0 && flightPrice && flightPrice > 0 ? bundleTotalPrice + flightPrice : 0

  return <_BaseHotelFlightsToggleButton
    flightsAvailable={offer.flights.flightsEnabled}
    isMemberPrice={showMemberPrice}
    hotelTotalPrice={hotelTotalPrice}
    hotelFlightTotalPrice={hotelFlightTotalPrice}
    pricesLoading={fetchingRates}
  />
}

export default withOfferPageAndReduxState((state) => ({
  offer: state.bedbankOffer,
  rooms: state.rooms,
  checkIn: state.checkInDate,
  checkOut: getCheckoutDate(state),
  flightOrigin: state.airport,
}), BedbankHotelFlightsToggleButton)
